<template>
  <div class="mb-9">
    <div
      class="text-md mb-5 font-medium leading-5 tracking-wider underline md:mb-6 md:text-lg md:uppercase md:leading-6 md:tracking-normal"
    >
      {{ $t('pdp.product_details_characteristics') }}
    </div>
    <span
      v-if="productOrderNumber && showPdpSellingPoints"
      class="mb-4 text-base text-gray-secondary md:block"
    >
      {{ $t('pdp.main_order_number') }}
      <span data-test-id="product-order-number">{{ productOrderNumber }}</span>
    </span>
    <p
      v-if="productDescription"
      class="text-md mb-12 font-normal leading-5 md:mb-6 md:text-lg md:leading-6"
    >
      {{ productDescription }}
    </p>
    <ProductBenefitsNew
      v-if="!showPdpSellingPoints && product"
      :product="product"
    />
  </div>
  <div class="mb-7 flex flex-col gap-5 md:gap-4">
    <div v-for="(otherPoint, index) in otherPoints" :key="index">
      <span class="text-md mb-1 font-normal leading-5 md:text-lg md:leading-6">
        {{ $t(otherPoint.label) }}:
      </span>
      <p class="text-base font-normal leading-4 md:text-base md:leading-5">
        {{ otherPoint.value }}
      </p>
    </div>
  </div>
  <div
    v-if="articleFeatureIcons.length"
    class="flex items-center pt-3 text-sm font-normal leading-5 md:text-lg md:leading-6"
  >
    <span class="mr-1">{{ $t('pdp.product_details_functions.details') }} </span>
    <NuxtLazyHydrate>
      <ProductFunctionIcons :symbols="articleFeatureIcons" />
    </NuxtLazyHydrate>
  </div>
  <div
    v-if="careInfo.length"
    class="flex items-center gap-7 border-t pt-4 text-base font-normal leading-5 md:text-lg md:leading-6"
  >
    <span>{{ $t('pdp.product_details_care') }}:</span>
    <ProductCareSymbols :symbols="careInfo" />
  </div>
  <div
    v-if="hasCreditorDetails"
    class="mt-7 flex flex-col font-normal leading-5"
  >
    <div class="mb-1 text-lg leading-6">
      {{ $t('pdp.product_creditor_details') }}
    </div>
    <div v-if="creditorCreatorName" class="mb-1 text-base leading-5">
      {{ creditorCreatorName }}
    </div>
    <div v-if="creditorAddress" class="text-base leading-5">
      {{ creditorAddress }}
    </div>
    <div v-if="creditorCityZip" class="text-base leading-5">
      {{ creditorCityZip }}
    </div>
    <div v-if="creditorCountry" class="mb-1 text-base leading-5">
      {{ creditorCountry }}
    </div>
    <div v-if="creditorWebsite" class="text-base leading-5">
      {{ creditorWebsite }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import { getFirstAttributeValue } from '@scayle/storefront-nuxt'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import {
  getAdvancedAttributeFirstValue,
  getProductDescription,
} from '#layers/base/utils/advancedAttribute'
import useUiState from '~/composables/ui/useUiState'
import { careSymbols } from '~/config/careSymbols'

type AttributeValue = string | number | null | undefined
interface OtherPointInterface {
  label: string
  value: AttributeValue
}

const props = defineProps({
  product: {
    type: Object as PropType<Product | null>,
    default: () => null,
  },
  productOrderNumber: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
})
const { activeVariant } = useUiState()
const { showPdpSellingPoints } = useExperiments()

// Product Description
const productDescription = computed<AttributeValue>(() => {
  return getProductDescription(props.product)
})

// Other Points
const otherPoints = computed<OtherPointInterface[]>(() => {
  const otherPointArray = [
    {
      label: 'pdp.product_details_material',
      value: getAdvancedAttributeFirstValue(props.product, 'material'),
    },
    {
      label: 'pdp.product_details_measures',
      value: getAdvancedAttributeFirstValue(
        activeVariant.value as Variant,
        'measures',
      ),
    },
    {
      label: 'pdp.product_details_wash_instructions',
      value: getFirstAttributeValue(props.product?.attributes, 'washComment')
        ?.label,
    },
  ]
  return otherPointArray.filter(
    (otherPoint) => otherPoint.value !== '' && otherPoint.value !== undefined,
  )
})
// Care Info
const careInfo = computed<string[]>(() =>
  [
    'caresymbolWash',
    'caresymbolBleaching',
    'caresymbolDry',
    'caresymbolIron',
    'caresymbolClean',
  ]
    .map(
      (attribute: string) =>
        getFirstAttributeValue(props.product?.attributes, attribute)?.value,
    )
    .filter((i): i is string => Boolean(i))
    .filter((i) => careSymbols[i]),
)

const getPathWithoutExtension = (val: string) => {
  const arr = val.split('.')
  arr.pop()
  return arr.join('')
}
const articleFeatureIcons = computed(() => {
  const values = props.product?.attributes?.articleFeatureIcon?.values
  if (!values) {
    return []
  }
  if (Array.isArray(values)) {
    return values.map((i) => getPathWithoutExtension(i.label))
  }
  return [getPathWithoutExtension(values.label)]
})

// Creditor / creator details from advancedAttributes (fallback: brand for name)
const creditorCreatorName = computed(
  () =>
    getAdvancedAttributeFirstValue(props.product, 'creditorName') as
      | string
      | null
      | undefined,
)

const creditorAddress = computed(
  () =>
    getAdvancedAttributeFirstValue(props.product, 'creditorAdress') as
      | string
      | null
      | undefined,
)

const creditorCityZip = computed(() => {
  const postCode = getAdvancedAttributeFirstValue(
    props.product,
    'creditorPostCode',
  ) as string | null | undefined
  const city = getAdvancedAttributeFirstValue(props.product, 'creditorCity') as
    | string
    | null
    | undefined
  if (postCode && city) {
    return `${postCode} ${city}`
  }
  if (postCode) {
    return postCode
  }
  if (city) {
    return city
  }
  return null
})

const creditorCountry = computed(
  () =>
    getAdvancedAttributeFirstValue(props.product, 'creditorCountry') as
      | string
      | null
      | undefined,
)

const creditorWebsite = computed(() => {
  const web = getAdvancedAttributeFirstValue(props.product, 'creditorWeb') as
    | string
    | null
    | undefined
  if (!web) {
    return null
  }
  if (web.includes('@')) {
    return web
  }
  return web.startsWith('http') ? web : `https://${web}`
})

const hasCreditorDetails = computed(
  () =>
    creditorCreatorName.value ||
    creditorAddress.value ||
    creditorCityZip.value ||
    creditorCountry.value ||
    creditorWebsite.value,
)
</script>
